import type {
  InsulinActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"
import styles from "src/styles/entryModal.module.scss"

interface Props {
  activity?: DeepPartial<InsulinActivity>;
  updateActivity: (a: DeepPartial<InsulinActivity>) => void;
  readOnly?: boolean;
}

const polyglotPrefix = "components.entry_modal.insulin.units."
const id = "insulin-units-range-input"
const max = 100
const min = 0
const sliderStep = 1
const inputStep = 0.01 // Display up to 2 decimal places

/*
 * renders a number label and range input for choosing number of units
 */
export function InsulinFormUnitsRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
    readOnly = false,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (numUnits: number | null) => {
      updateActivity({
        num_units: typeof numUnits === "number"
          ? numUnits
          : undefined,
      })
    },
    [updateActivity],
  )

  const value = typeof activity?.num_units === "number"
    ? activity.num_units
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    value,
    inputStep,
    sliderStep,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  //const step = serverInsulinToUserInsulin(activity.insulin_med.precision);

  return (
    <div className={styles.row}>
      <SliderValue sizePreset="large">
        <SliderValueInput
          max={max}
          min={min}
          step={inputStep}
          id={id}
          value={inputValue}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
          readOnly={readOnly}
        />
        <SliderValueLabelContainer>
          <SliderValueLabel htmlFor={id}>
            {polyglot.t(
              `${polyglotPrefix}label`,
              activity?.num_units ?? 0,
            )}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            min={min}
            max={max}
            step={sliderStep}
            id={id}
            value={sliderValue ?? min}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      )}
    </div>
  )
}
